import {useParams} from 'react-router-dom'
import React, { useState , useMemo, useEffect, useRef} from 'react';
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";
import profileImage from "../../src/assets/profile.png"
// Use shared TeamLogo component for proper logo handling
import { TeamLogo } from '../components/common/TeamLogo';
import { LeagueAvatar } from '../components/league/LeagueAvatar';
import TransferWindowToggle from '../components/settings/TransferWindowToggle';
import {
    useGetLeagueBySlug,
    useUpdateLeague,
    useInviteToLeague,
    useKickFromLeague
} from '../api/leaguesApi';
import { useActiveLeagueContext } from '../context/ActiveLeagueContext';
import { AuthContext } from '../context/AuthContext';
import { useContext } from 'react';
import { toast } from 'react-hot-toast';
import {
    ALLOWED_LEAGUE_AVATAR_TYPES,
    LEAGUE_AVATAR_ACCEPT,
    MAX_LEAGUE_AVATAR_SIZE_BYTES
} from '../utils/leagueAvatar';
import { parseInviteEmails } from '../utils/inviteEmails';
import { getInviteDeliveryFailureMessage, getOwnerJoinabilityErrorMessage } from '../utils/inviteErrors';

const getEntityId = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
        return value._id || value.id || null;
    }
    return value;
};

const KICKOFF_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DEFAULT_KICKOFF_TIME = '15:00';
const DEFAULT_MATCH_TIMEZONE = 'Europe/Paris';
const KICKOFF_PREVIEW_REFRESH_MS = 30 * 1000;

const normalizeKickoffTime = (value) => (
    typeof value === 'string' ? value.trim() : ''
);

const getSafeKickoffTime = (value) => {
    const normalizedValue = normalizeKickoffTime(value);
    return KICKOFF_TIME_PATTERN.test(normalizedValue)
        ? normalizedValue
        : DEFAULT_KICKOFF_TIME;
};

const getSafeMatchTimezone = (value) => {
    if (typeof value !== 'string' || value.trim().length === 0) {
        return DEFAULT_MATCH_TIMEZONE;
    }

    const normalizedValue = value.trim();

    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: normalizedValue }).format(new Date(0));
        return normalizedValue;
    } catch {
        return DEFAULT_MATCH_TIMEZONE;
    }
};

const getTimeZoneParts = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(date);

    const values = parts.reduce((result, part) => {
        if (part.type !== 'literal') {
            result[part.type] = part.value;
        }
        return result;
    }, {});

    return {
        year: Number(values.year),
        month: Number(values.month),
        day: Number(values.day),
        hour: Number(values.hour),
        minute: Number(values.minute)
    };
};

const addDaysToTimeZoneDate = (localDate, daysToAdd) => {
    const date = new Date(Date.UTC(
        localDate.year,
        localDate.month - 1,
        localDate.day + daysToAdd,
        12,
        0,
        0,
        0
    ));

    return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate()
    };
};

const matchesTimeZoneClockTime = (date, localDate, hour, minute, timeZone) => {
    const local = getTimeZoneParts(date, timeZone);
    return (
        local.year === localDate.year &&
        local.month === localDate.month &&
        local.day === localDate.day &&
        local.hour === hour &&
        local.minute === minute
    );
};

const getTimeZoneOffsetMinutes = (date, timeZone) => {
    const local = getTimeZoneParts(date, timeZone);
    const localAsUtc = Date.UTC(
        local.year,
        local.month - 1,
        local.day,
        local.hour,
        local.minute,
        0,
        0
    );

    return (localAsUtc - date.getTime()) / 60000;
};

const findExactUtcForTimeZoneClockTime = (localDate, hour, minute, timeZone) => {
    const targetAsUtc = Date.UTC(
        localDate.year,
        localDate.month - 1,
        localDate.day,
        hour,
        minute,
        0,
        0
    );
    let candidateTime = targetAsUtc;
    const seenCandidateTimes = new Set();

    for (let attempt = 0; attempt < 5; attempt += 1) {
        if (seenCandidateTimes.has(candidateTime)) break;
        seenCandidateTimes.add(candidateTime);

        const candidate = new Date(candidateTime);
        if (matchesTimeZoneClockTime(candidate, localDate, hour, minute, timeZone)) {
            return candidate;
        }

        const offsetMinutes = getTimeZoneOffsetMinutes(candidate, timeZone);
        candidateTime = targetAsUtc - (offsetMinutes * 60000);
    }

    const searchStart = targetAsUtc - (36 * 60 * 60000);
    const searchEnd = targetAsUtc + (36 * 60 * 60000);

    for (let time = searchStart; time <= searchEnd; time += 60000) {
        const candidate = new Date(time);
        if (matchesTimeZoneClockTime(candidate, localDate, hour, minute, timeZone)) {
            return candidate;
        }
    }

    return null;
};

const findNextValidUtcForTimeZoneClockTime = (localDate, hour, minute, timeZone) => {
    const targetAsUtc = Date.UTC(
        localDate.year,
        localDate.month - 1,
        localDate.day,
        hour,
        minute,
        0,
        0
    );
    const targetLocalMinutes = (hour * 60) + minute;
    const searchStart = targetAsUtc - (36 * 60 * 60000);
    const searchEnd = targetAsUtc + (36 * 60 * 60000);

    for (let time = searchStart; time <= searchEnd; time += 60000) {
        const candidate = new Date(time);
        const local = getTimeZoneParts(candidate, timeZone);
        const candidateLocalMinutes = (local.hour * 60) + local.minute;

        if (
            local.year === localDate.year &&
            local.month === localDate.month &&
            local.day === localDate.day &&
            candidateLocalMinutes >= targetLocalMinutes
        ) {
            return candidate;
        }
    }

    return new Date(targetAsUtc);
};

const buildKickoffDate = (baseDate, daysAhead, hour, minute, timeZone) => {
    const localDate = getTimeZoneParts(baseDate, timeZone);
    const targetLocalDate = addDaysToTimeZoneDate(localDate, daysAhead);

    return (
        findExactUtcForTimeZoneClockTime(targetLocalDate, hour, minute, timeZone) ||
        findNextValidUtcForTimeZoneClockTime(targetLocalDate, hour, minute, timeZone)
    );
};

const getNextKickoffDate = (kickoffTime, timeZone, now = new Date()) => {
    const normalizedKickoffTime = normalizeKickoffTime(kickoffTime);
    if (!KICKOFF_TIME_PATTERN.test(normalizedKickoffTime)) {
        return null;
    }

    const [hour, minute] = normalizedKickoffTime.split(':').map(Number);

    for (let daysAhead = 0; daysAhead <= 1; daysAhead += 1) {
        const candidate = buildKickoffDate(now, daysAhead, hour, minute, timeZone);
        if (candidate > now) return candidate;
    }

    return buildKickoffDate(now, 1, hour, minute, timeZone);
};

const formatRemainingTime = (startDate, now = new Date()) => {
    const remainingMinutes = Math.max(
        1,
        Math.ceil((startDate.getTime() - now.getTime()) / (60 * 1000))
    );
    const days = Math.floor(remainingMinutes / (24 * 60));
    const hours = Math.floor((remainingMinutes % (24 * 60)) / 60);
    const minutes = remainingMinutes % 60;

    return `${days} ${days === 1 ? 'day' : 'days'} ${hours} hrs ${minutes} mins`;
};

const getKickoffPreviewText = (kickoffTime, timeZone, now = new Date()) => {
    const nextKickoffDate = getNextKickoffDate(kickoffTime, timeZone, now);
    if (!nextKickoffDate) {
        return `Using ${timeZone} time. Choose a valid kickoff time to see the countdown.`;
    }

    return `Using ${timeZone} time. Next match starts in ${formatRemainingTime(nextKickoffDate, now)}.`;
};

function LeagueSettings() {
    const { slug } = useParams();
    const { setActiveLeague } = useActiveLeagueContext();
    const { user } = useContext(AuthContext);
    const { data: leagueData, isLoading, error } = useGetLeagueBySlug(slug);

    // Handle both possible API response shapes:
    // 1) { league: { ... } }
    // 2) { data: { league: { ... } } } (legacy)
    const league = useMemo(() => {
        // axios response wrapper
        const raw = leagueData?.data;
        if (!raw) return undefined;
        if (raw.league) return raw.league;        // Preferred shape
        return raw;                              // Fallback when backend returns league object directly
    }, [leagueData]);

    const updateLeagueMutation = useUpdateLeague();
    const inviteToLeagueMutation = useInviteToLeague();
    const kickFromLeagueMutation = useKickFromLeague();
    const leagueAvatarInputRef = useRef(null);
    const leagueAvatarReadTokenRef = useRef(0);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentTime, setCurrentTime] = useState(() => new Date());
    const [leagueAvatarFile, setLeagueAvatarFile] = useState(null);
    const [leagueAvatarPreview, setLeagueAvatarPreview] = useState(null);
    
    // Initialize with minimal placeholder data
    const [formData, setFormData] = useState({
        name: '',
        teams: 20,
        freeJoin: false,
        matchKickoffTime: DEFAULT_KICKOFF_TIME,
        users: []
    });
    
    // Update formData when league data is fetched
    useEffect(() => {
        if (league) {
            const l = league;
            leagueAvatarReadTokenRef.current += 1;
            setLeagueAvatarFile(null);
            setLeagueAvatarPreview(l.image || null);
            if (leagueAvatarInputRef.current) {
                leagueAvatarInputRef.current.value = '';
            }
            setFormData({
                name: l.name || '',
                teams: l.amountOfTeams || 20,
                freeJoin: l.freeJoin || false,
                matchKickoffTime: getSafeKickoffTime(l.matchKickoffTime),
                users: l.teams?.map((team) => ({
                    id: team._id,                       // Team ID for UI key/reference
                    name: team.owner?.username || team.name,
                    // Preserve the actual logo path returned by backend. Fallback to placeholder if missing.
                    avatar: team.logo || profileImage,
                    teamId: team._id,
                    ownerId: team.owner?._id
                })) || []
            });
        }
    }, [league]);

    const resetLeagueAvatarSelection = (inputElement = leagueAvatarInputRef.current) => {
        leagueAvatarReadTokenRef.current += 1;
        setLeagueAvatarFile(null);
        setLeagueAvatarPreview(league?.image || null);
        if (inputElement) {
            inputElement.value = '';
        }
    };

    const handleLeagueAvatarChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            resetLeagueAvatarSelection(event.target);
            return;
        }

        if (!ALLOWED_LEAGUE_AVATAR_TYPES.has(file.type)) {
            toast.error('League avatar must be a JPG or PNG image');
            resetLeagueAvatarSelection(event.target);
            return;
        }

        if (file.size > MAX_LEAGUE_AVATAR_SIZE_BYTES) {
            toast.error('League avatar must be 5 MB or smaller');
            resetLeagueAvatarSelection(event.target);
            return;
        }

        const readToken = leagueAvatarReadTokenRef.current + 1;
        leagueAvatarReadTokenRef.current = readToken;
        setLeagueAvatarFile(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            if (leagueAvatarReadTokenRef.current === readToken) {
                setLeagueAvatarPreview(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const clearSelectedLeagueAvatar = () => {
        resetLeagueAvatarSelection();
    };

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setCurrentTime(new Date());
        }, KICKOFF_PREVIEW_REFRESH_MS);

        return () => window.clearInterval(intervalId);
    }, []);
    
    const leagueOwnerId = getEntityId(league?.owner);
    const resolvedLeagueId = getEntityId(league?._id);
    const resolvedLeagueSeason = typeof league?.season === 'number'
        ? league.season
        : 1;

    // Check if current user is the league owner
    const isLeagueOwner = !!leagueOwnerId && !!user?._id && leagueOwnerId.toString() === user._id.toString();
    const matchKickoffTimezone = getSafeMatchTimezone(league?.matchKickoffTimezone);
    const kickoffPreviewText = getKickoffPreviewText(
        formData.matchKickoffTime,
        matchKickoffTimezone,
        currentTime
    );
    const kickoffDescribedBy = [
        'match-kickoff-time-help',
        !isLeagueOwner ? 'readonly-notice' : null
    ].filter(Boolean).join(' ');

    // Custom styles for react-select dropdown
    const selectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: '#191D37',
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: 'none',
            minHeight: '44px',
            borderRadius: '10px',
            '&:hover': {
                border: '1px solid #4F7BFF',
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#B0B0B0',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#ffffff',
        }),
        input: (provided) => ({
            ...provided,
            color: '#ffffff',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#4F7BFF' : '#191D37',
            color: '#ffffff',
            cursor: 'pointer',
            '&:active': {
                backgroundColor: '#4F7BFF',
            },
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#191D37',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '10px',
            overflow: 'hidden',
            zIndex: 20
        }),
        menuList: (provided) => ({
            ...provided,
            padding: 0,
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#B0B0B0',
            '&:hover': {
                color: '#ffffff',
            },
        }),
    };

    // Team count options for dropdown
    const teamOptions = useMemo(() => {
        const baseOptions = [4, 6, 8, 12, 14, 16, 18, 20];
        const currentTeamCount = Number(formData.teams);
        const optionValues = (
            Number.isFinite(currentTeamCount) &&
            Number.isInteger(currentTeamCount) &&
            currentTeamCount >= 2 &&
            !baseOptions.includes(currentTeamCount)
        )
            ? [...baseOptions, currentTeamCount]
            : baseOptions;

        return optionValues
            .sort((a, b) => a - b)
            .map(v => ({
                value: v,
                label: `${v} teams`
            }));
    }, [formData.teams]);

    const [invites, setInvites] = useState('');
    const [emailsValid, setEmailsValid] = useState(true);

    const handleInvites = (event) => {
        setInvites(event.target.value);
        setEmailsValid(true);
    }

    const sendInvites = async () => {
        const validEmails = parseInviteEmails(invites);
        setEmailsValid(Boolean(validEmails));
        
        if (!validEmails) {
            toast.error('Please enter valid email addresses');
            return;
        }
        
        if (!isLeagueOwner) {
            toast.error('Only the league owner can send invites');
            return;
        }
        
        try {
            const inviteResult = await inviteToLeagueMutation.mutateAsync({
                leagueId: league?._id,
                emails: validEmails,
                slug: league?.slug
            });
            
            // Update ActiveLeagueContext if league data was returned
            if (inviteResult?.league && setActiveLeague) {
                setActiveLeague(inviteResult.league);
            }
            
            toast.success(`Invites sent to ${validEmails.length} email(s)`);
            setInvites(''); // Clear the textarea after successful invite
        } catch (error) {
            console.error('Error sending invites:', error);
            // A 409 with a joinabilityReason means nobody new can join; the
            // code is the contract, so show our own wording for it.
            const joinabilityMessage = getOwnerJoinabilityErrorMessage(error);
            const deliveryFailureMessage = getInviteDeliveryFailureMessage(error);
            if (joinabilityMessage) {
                toast.error(joinabilityMessage);
            } else if (deliveryFailureMessage) {
                toast.error(deliveryFailureMessage);
            } else if (error.response?.status === 400) {
                toast.error(error.response.data.error || error.response.data.message || 'Invalid email addresses');
            } else if (error.response?.status === 409) {
                toast.error('Some users are already members or have pending invites');
            } else if (error.response?.status === 429) {
                toast.error('Too many invites sent. Please try again later');
            } else {
                toast.error('Failed to send invites. Please try again');
            }
        }
    }

    const handleChangeAmount = (selectedOption) => {
        const newAmount = selectedOption.value;

        // Validate that new amount is not less than current team count
        const currentTeamCount = league?.teams?.length || 0;
        if (newAmount < currentTeamCount) {
            toast.error(`Cannot set team limit below current team count (${currentTeamCount})`);
            return;
        }

        setFormData({
            ...formData,
            teams: newAmount
        });
    };

    const removeUserById = (users, id) => {
        return users.filter(user => user.id !== id);
    }

    const handleUsers = async (userId) => {
        if (!isLeagueOwner) {
            toast.error('Only the league owner can remove members');
            return;
        }
        
        // Find the user to get their team ID
        const userToRemove = formData.users.find(user => user.id === userId);
        if (!userToRemove) {
            toast.error('User not found');
            return;
        }
        
        // Prevent owner from kicking themselves
        if (userToRemove.ownerId === user?._id) {
            toast.error('You cannot remove yourself from the league');
            return;
        }
        
        // Show confirmation dialog
        if (!window.confirm(`Are you sure you want to remove ${userToRemove.name} from the league? This action cannot be undone.`)) {
            return;
        }
        
        try {
            const kickResult = await kickFromLeagueMutation.mutateAsync({
                leagueId: league?._id,
                teamId: userToRemove.teamId,
                slug: league?.slug
            });
            
            // Update ActiveLeagueContext if league data was returned
            if (kickResult?.league && setActiveLeague) {
                setActiveLeague(kickResult.league);
            }
            
            toast.success(`${userToRemove.name} has been removed from the league`);
        } catch (error) {
            console.error('Error removing user:', error);
            if (error.response?.status === 403) {
                toast.error('You do not have permission to remove this member');
            } else if (error.response?.status === 404) {
                toast.error('Team not found');
            } else {
                toast.error('Failed to remove member. Please try again');
            }
        }
    }


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isLeagueOwner) {
            toast.error('Only the league owner can update settings');
            return;
        }

        const normalizedKickoffTime = normalizeKickoffTime(formData.matchKickoffTime);
        if (!KICKOFF_TIME_PATTERN.test(normalizedKickoffTime)) {
            toast.error('Please choose a valid kickoff time');
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Create FormData for multipart upload
            const updateData = new FormData();
            
            // Add text fields
            updateData.append('name', formData.name);
            // Only append amountOfTeams if it has changed
            if (formData.teams !== league?.amountOfTeams) {
                updateData.append('amountOfTeams', formData.teams);
            }
            updateData.append('freeJoin', formData.freeJoin);
            updateData.append('matchKickoffTime', normalizedKickoffTime);
            if (leagueAvatarFile) {
                updateData.append('avatar', leagueAvatarFile);
            }
            
            const updatedLeague = await updateLeagueMutation.mutateAsync({
                leagueId: league?._id,
                formData: updateData,
                slug: league?.slug
            });
            
            // Update ActiveLeagueContext with the updated league data
            if (updatedLeague?.league && setActiveLeague) {
                // Ensure `teams` remains an array before placing the object in context.
                // The PATCH endpoint currently returns the team *count* (number) instead
                // of the array, which breaks pages that expect `.teams.map` or `.teams.length`.
                const normalisedLeague = {
                    ...updatedLeague.league,
                    teams: Array.isArray(updatedLeague.league.teams)
                        ? updatedLeague.league.teams
                        : (league?.teams || [])
                };
                setActiveLeague(normalisedLeague);
                leagueAvatarReadTokenRef.current += 1;
                setLeagueAvatarFile(null);
                setLeagueAvatarPreview(normalisedLeague.image || null);
                if (leagueAvatarInputRef.current) {
                    leagueAvatarInputRef.current.value = '';
                }
            }
            
            toast.success('League settings updated successfully');
        } catch (error) {
            console.error('Error updating league:', error);
            if (error.response?.status === 409) {
                toast.error('League name already exists');
            } else if (error.response?.status === 400) {
                toast.error(error.response.data.error || 'Invalid input');
            } else {
                toast.error('Failed to update league settings');
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    const UsersList = ({ users }) => {
        if (!users || users.length === 0) {
            return (
                <div className="users-list-modern">
                    <div className="users-empty-state">
                        <div className="empty-icon">👥</div>
                        <p className="empty-text">No members yet. Send some invites!</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="users-list-modern">
                {users.map((item, index) => (
                    <div key={item.id} className="user-item-modern">
                        <div className="user-rank">{index + 1}</div>
                        <TeamLogo logoUrl={item.avatar} teamName={item.name} className="user-avatar" />
                        <div className="user-info">
                            <div className="user-name">{item.name}</div>
                            {item.ownerId === league?.owner && (
                                <div className="user-role">Owner</div>
                            )}
                        </div>
                        {isLeagueOwner && item.ownerId !== user?._id && (
                            <button
                                type="button"
                                className="remove-action"
                                onClick={() => handleUsers(item.id)}
                                disabled={isSubmitting || kickFromLeagueMutation.isPending}
                                aria-label={`Remove ${item.name} from league`}
                            >
                                {kickFromLeagueMutation.isPending ? 'Removing...' : 'Remove'}
                            </button>
                        )}
                    </div>
                ))}
            </div>
        );
    };


    // Loading state
    if (isLoading) {
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>LEAGUE SETTINGS</h1>
                    <div className="league-settings-wrapper-modern">
                        <div className="league-settings-card">
                            <p style={{textAlign: 'center', padding: '40px 0'}}>Loading league data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>LEAGUE SETTINGS</h1>
                    <div className="league-settings-wrapper-modern">
                        <div className="league-settings-card">
                            <p style={{textAlign: 'center', padding: '40px 0'}}>Error loading league data. Please try again later.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // No league data
    if (!league) {
        return (
            <div className="page-login">
                <div className="wrapper">
                    <h1>LEAGUE SETTINGS</h1>
                    <div className="league-settings-wrapper-modern">
                        <div className="league-settings-card">
                            <p style={{textAlign: 'center', padding: '40px 0'}}>League not found.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-login">
            <div className="wrapper">
                <h1>LEAGUE SETTINGS</h1>

                {!isLeagueOwner && (
                    <div id="readonly-notice" className="league-settings-card" style={{marginBottom: '20px', backgroundColor: 'rgba(79, 123, 255, 0.1)', border: '1px solid rgba(79, 123, 255, 0.3)'}} role="alert">
                        <p style={{margin: 0, fontSize: '14px'}}>You are viewing league settings in read-only mode. Only the league owner can make changes.</p>
                    </div>
                )}

                <div className="league-settings-wrapper-modern">
                    <div className="league-settings-layout">
                        {/* Main Settings Column */}
                        <div className="settings-main">
                            {/* Card 1: League Identity */}
                            <div className="league-settings-card">
                                <div className="card-header">
                                    <h3>League Identity</h3>
                                </div>
                                <div className="card-content">
                                    <div className="avatar-upload-modern">
                                        <div className="avatar-upload-center">
                                            <div className="avatar-preview">
                                                <LeagueAvatar
                                                    image={leagueAvatarPreview}
                                                    name={formData.name || league?.name}
                                                />
                                                {leagueAvatarFile && (
                                                    <button
                                                        type="button"
                                                        className="clear-btn"
                                                        onClick={clearSelectedLeagueAvatar}
                                                        disabled={!isLeagueOwner || isSubmitting}
                                                        aria-label="Clear selected league avatar"
                                                    >
                                                        <span className="ico icon-s-cross" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="avatar-actions">
                                                <input
                                                    ref={leagueAvatarInputRef}
                                                    id="league-avatar-input"
                                                    className="avatar-file-input"
                                                    type="file"
                                                    accept={LEAGUE_AVATAR_ACCEPT}
                                                    onChange={handleLeagueAvatarChange}
                                                    disabled={!isLeagueOwner || isSubmitting}
                                                    aria-label="League avatar"
                                                    aria-describedby="league-avatar-help"
                                                />
                                                <label
                                                    htmlFor="league-avatar-input"
                                                    className={`upload-label ${!isLeagueOwner || isSubmitting ? 'disabled' : ''}`}
                                                >
                                                    <span className="ico icon-file" />
                                                    Choose Avatar
                                                </label>
                                                <p id="league-avatar-help" className="upload-hint">
                                                    JPG or PNG, max 5 MB
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="league-name">League Name</label>
                                        <input
                                            id="league-name"
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                            disabled={!isLeagueOwner || isSubmitting}
                                            aria-label="League name"
                                            aria-describedby={!isLeagueOwner ? "readonly-notice" : undefined}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Configuration */}
                            <div className="league-settings-card">
                                <div className="card-header">
                                    <h3>Configuration</h3>
                                    <span className="card-badge">{formData.users.length}/{formData.teams} teams</span>
                                </div>
                                <div className="card-content">
                                    <div className="form-group">
                                        <label>Maximum Teams</label>
                                        <Select
                                            styles={selectStyles}
                                            options={teamOptions}
                                            value={teamOptions.find(o => o.value === Number(formData.teams))}
                                            onChange={handleChangeAmount}
                                            isDisabled={!isLeagueOwner || isSubmitting}
                                            aria-label="Select maximum number of teams"
                                        />
                                    </div>

                                    <div className="free-join-toggle">
                                        <div className="toggle-label">
                                            <span>Allow anyone to join</span>
                                            <p className="toggle-description">Users can join without an invitation</p>
                                        </div>
                                        <div className="switcher">
                                            <input
                                                type="checkbox"
                                                id="free-join"
                                                checked={formData.freeJoin}
                                                onChange={(e) => setFormData({...formData, freeJoin: e.target.checked})}
                                                disabled={!isLeagueOwner || isSubmitting}
                                                aria-describedby="freeJoin-help"
                                            />
                                            <label htmlFor="free-join" />
                                        </div>
                                        <div id="freeJoin-help" className="sr-only">
                                            When enabled, users can join this league without requiring an invitation
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="match-kickoff-time">Match Kickoff Time</label>
                                        <input
                                            id="match-kickoff-time"
                                            className="kickoff-time-input"
                                            type="time"
                                            required
                                            value={formData.matchKickoffTime}
                                            onChange={(e) => setFormData({...formData, matchKickoffTime: e.target.value})}
                                            disabled={!isLeagueOwner || isSubmitting}
                                            aria-label="Match kickoff time"
                                            aria-describedby={kickoffDescribedBy}
                                        />
                                        <p id="match-kickoff-time-help" className="kickoff-time-help">
                                            {kickoffPreviewText}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Card 3: Invite Members */}
                            <div className="league-settings-card">
                                <div className="card-header">
                                    <h3>Invite Members</h3>
                                </div>
                                <div className="card-content">
                                    <div className="invite-section-modern">
                                        <div className="invite-input-wrapper">
                                            <textarea
                                                id="invite-emails"
                                                placeholder="Enter email addresses, separated by commas or new lines..."
                                                value={invites}
                                                onChange={handleInvites}
                                                disabled={!isLeagueOwner || isSubmitting || inviteToLeagueMutation.isPending}
                                                aria-label="Email addresses to invite"
                                                aria-describedby={!emailsValid ? "email-validation-error" : "email-help"}
                                            />
                                        </div>
                                        <div className="invite-footer">
                                            <span className="email-hint">Separate multiple emails with commas or new lines</span>
                                            <button
                                                type="button"
                                                className="invite-send-btn"
                                                onClick={sendInvites}
                                                disabled={!isLeagueOwner || isSubmitting || inviteToLeagueMutation.isPending || !emailsValid || !invites.trim()}
                                                aria-label="Send invitations to email addresses"
                                            >
                                                {inviteToLeagueMutation.isPending ? 'Sending...' : 'Send Invites'}
                                            </button>
                                        </div>
                                        {!emailsValid && (
                                            <div className="validation-error" id="email-validation-error" role="alert">
                                                Please enter valid email addresses
                                            </div>
                                        )}
                                        <div id="email-help" className="sr-only">
                                            Enter email addresses separated by commas or new lines to invite users to this league
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 4: Transfer Window (admin only) */}
                            <TransferWindowToggle
                                leagueId={resolvedLeagueId}
                                leagueSeason={resolvedLeagueSeason}
                                isLeagueOwner={isLeagueOwner}
                            />

                            {/* Save Button */}
                            <div className="league-settings-submit">
                                <button
                                    type="button"
                                    className="submit-btn"
                                    onClick={handleSubmit}
                                    disabled={!isLeagueOwner || isSubmitting}
                                    aria-label="Save league settings changes"
                                >
                                    {isSubmitting ? 'UPDATING...' : 'SAVE CHANGES'}
                                </button>
                            </div>
                        </div>

                        {/* Sidebar: Members */}
                        <div className="settings-sidebar">
                            <div className="league-settings-card">
                                <div className="card-header">
                                    <h3>Members</h3>
                                    <span className="card-badge">{formData.users.length}/{formData.teams}</span>
                                </div>
                                <div className="card-content">
                                    <UsersList users={formData.users} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export {LeagueSettings}
